"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { Fragment } from "react";
import { ThemeToggle } from "@/components/theme-toggle";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Separator } from "@/components/ui/separator";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { useAuth } from "@/lib/auth";

const routeLabels: Record<string, string> = {
  "/dashboard": "Dashboard",
  "/manage/org": "Organization",
  "/manage/members": "Members",
  "/manage/projects": "Projects",
  "/manage/account": "Account",
  "/manage/billing": "Billing",
  "/manage/settings": "Settings",
  "/usage": "Usage",
  "/projects": "Projects",
  "/team": "Team",
  "/compute": "Compute",
  "/networking": "Networking",
  "/help": "Help",
  "/deploy": "Deploy",
  "/models": "Models",
  "/keys": "API keys",
  "/admin": "Admin",
  "/admin/flags": "Flags",
  "/admin/orgs": "Orgs",
  "/admin/capacity": "Orgs",
  "/admin/credits": "Credits",
  "/admin/users": "Users",
  "/admin/tools": "Tools",
  "/admin/dashboard": "Dashboard",
  "/admin/team": "Team",
  "/admin/support": "Support",
  "/admin/hardware": "Hardware",
};

export function SiteHeader() {
  const pathname = usePathname();
  const { billingSummary } = useAuth();
  const planName = billingSummary?.plan_name ?? null;

  // Generate breadcrumb items from pathname
  const generateBreadcrumbs = () => {
    const segments = pathname.split("/").filter(Boolean);
    const breadcrumbs = [];

    // Always start with Home/Dashboard
    breadcrumbs.push({
      label: "Home",
      href: "/dashboard",
      isCurrentPage: pathname === "/dashboard",
    });

    // Handle project-specific routes
    if (segments[0] === "projects" && segments[1]) {
      const projectId = segments[1];
      const projectName = `Project ${projectId}`; // You can make this dynamic later

      breadcrumbs.push({
        label: projectName,
        href: `/projects/${projectId}`,
        isCurrentPage: segments.length === 2,
      });

      // Add sub-pages like monitoring
      if (segments[2]) {
        const subPageLabel =
          routeLabels[`/${segments[2]}`] || segments[2].charAt(0).toUpperCase() + segments[2].slice(1);
        breadcrumbs.push({
          label: subPageLabel,
          href: `/projects/${projectId}/${segments[2]}`,
          isCurrentPage: true,
        });
      }
    } else {
      // Handle other routes
      let currentPath = "";
      for (let i = 0; i < segments.length; i++) {
        currentPath += `/${segments[i]}`;
        const label = routeLabels[currentPath] || segments[i].charAt(0).toUpperCase() + segments[i].slice(1);
        const isCurrentPage = i === segments.length - 1;

        // Skip if it's the dashboard (already added as Home)
        if (currentPath !== "/dashboard") {
          breadcrumbs.push({
            label,
            href: currentPath,
            isCurrentPage,
          });
        }
      }
    }

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();

  return (
    <header className="flex h-(--header-height) shrink-0 items-center gap-2 border-b transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-(--header-height)">
      <div className="flex w-full items-center gap-1 px-4 lg:gap-2 lg:px-6">
        <SidebarTrigger className="-ml-1" />
        <Separator orientation="vertical" className="mx-2 data-[orientation=vertical]:h-4" />
        <Breadcrumb>
          <BreadcrumbList>
            {breadcrumbs.map((breadcrumb, index) => (
              <Fragment key={breadcrumb.href}>
                <BreadcrumbItem>
                  {breadcrumb.isCurrentPage ? (
                    <BreadcrumbPage>{breadcrumb.label}</BreadcrumbPage>
                  ) : breadcrumb.href === "/manage" ? (
                    <span className="text-muted-foreground">{breadcrumb.label}</span>
                  ) : (
                    <BreadcrumbLink asChild>
                      <Link href={breadcrumb.href}>{breadcrumb.label}</Link>
                    </BreadcrumbLink>
                  )}
                </BreadcrumbItem>
                {index < breadcrumbs.length - 1 && <BreadcrumbSeparator />}
              </Fragment>
            ))}
          </BreadcrumbList>
        </Breadcrumb>
        <div className="ml-auto flex items-center gap-4">
          {planName ? (
            <div className="hidden md:flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">Plan:</span>
              <span className="font-semibold">{planName}</span>
            </div>
          ) : null}
          <ThemeToggle />
        </div>
      </div>
    </header>
  );
}
